
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Earth, Leaf, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';

const VideoBackgroundSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // Function to handle video playback
    const playVideo = async () => {
      try {
        await videoElement.play();
        setIsPlaying(true);
        console.log("Video playback started successfully");
      } catch (error) {
        console.error("Error playing the video:", error);
        setIsPlaying(false);
      }
    };

    // Play video initially
    playVideo();

    // Monitor if video stops playing unexpectedly
    const handlePause = () => {
      console.log("Video paused, attempting to restart");
      if (!videoElement.ended && document.visibilityState === 'visible') {
        playVideo();
      }
    };

    // Event listeners to ensure continuous playback
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', playVideo); // Restart if it somehow ends
    
    // Check playback status periodically
    const playbackCheck = setInterval(() => {
      if (!isPlaying && document.visibilityState === 'visible') {
        playVideo();
      }
    }, 5000);

    return () => {
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', playVideo);
      clearInterval(playbackCheck);
    };
  }, [isPlaying]);

  return (
    <section className="relative w-full h-[500px] overflow-hidden">
      {/* Video background */}
      <div className="absolute inset-0 w-full h-full">
        <video 
          ref={videoRef}
          autoPlay 
          muted 
          loop 
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
        >
          <source 
            src="https://immobiliere-du-camp.fr/wp-content/uploads/2025/01/8996215-hd_1920_1080_30fps.mp4" 
            type="video/mp4" 
          />
          Your browser does not support the video tag.
        </video>
        
        {/* Overlay to ensure text visibility */}
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content overlay */}
      <div className="relative z-10 container mx-auto px-6 h-full flex flex-col items-center justify-center">
        <div className="max-w-2xl animate-fade-in text-center">
          <div className="flex items-center mb-4 text-patrimoine-gold justify-center">
            <Leaf className="h-6 w-6 mr-2" />
            <h3 className="text-lg md:text-xl font-medium">INVESTISSEMENT ÉCOLOGIQUE</h3>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-white mb-6 leading-tight">
            Investissez dans l'avenir avec nos EcoParks
          </h2>
          
          <p className="text-white/90 text-base md:text-lg mb-6 mx-auto">
            Découvrez nos parkings écologiques dotés de bornes de recharge électrique, 
            panneaux solaires et systèmes de récupération d'eau. Un investissement 
            rentable et responsable pour la planète.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8 justify-center">
            <div className="flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Sun className="h-5 w-5 text-patrimoine-gold mr-2" />
              <span className="text-white text-sm">Panneaux solaires intégrés</span>
            </div>
            <div className="flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Earth className="h-5 w-5 text-patrimoine-gold mr-2" />
              <span className="text-white text-sm">Certifié éco-responsable</span>
            </div>
          </div>
          
          <Link to="/bilan-patrimonial">
            <Button className="bg-patrimoine-gold text-black hover:bg-amber-400 font-medium transition-all">
              Découvrir nos EcoParks
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default VideoBackgroundSection;
